import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Services.css";

const services = [
  {
    tag: "Residential",
    title: "Residential Construction",
    shortTitle: "Residential",
    desc: "Comfortable, durable, and beautifully planned homes designed around your family's lifestyle and aspirations.",
    num: "01",
    angle: -120,
    svgIcon: (
      <svg viewBox="0 0 22 22" fill="none">
        <path
          d="M3 10L11 3l8 7v9a1 1 0 01-1 1H4a1 1 0 01-1-1V10z"
          stroke="#ffd43b"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <rect
          x="7.5"
          y="13"
          width="7"
          height="6"
          rx="0.8"
          stroke="#ffd43b"
          strokeWidth="1.4"
        />
      </svg>
    ),
  },
  {
    tag: "Commercial",
    title: "Commercial Construction",
    shortTitle: "Commercial",
    desc: "Professional commercial spaces built with practical planning, quality materials, and timely execution.",
    num: "02",
    angle: -60,
    svgIcon: (
      <svg viewBox="0 0 22 22" fill="none">
        <rect
          x="3"
          y="7"
          width="16"
          height="13"
          rx="1.2"
          stroke="#ffd43b"
          strokeWidth="1.5"
        />
        <path
          d="M7 7V5a4 4 0 018 0v2"
          stroke="#ffd43b"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <line
          x1="7"
          y1="12"
          x2="15"
          y2="12"
          stroke="#ffd43b"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
        <line
          x1="7"
          y1="15.5"
          x2="12"
          y2="15.5"
          stroke="#ffd43b"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    tag: "Renovation",
    title: "Renovation & Remodeling",
    shortTitle: "Renovation",
    desc: "Transform your existing space with modern upgrades, improved functionality, and refined finishes.",
    num: "03",
    angle: 0,
    svgIcon: (
      <svg viewBox="0 0 22 22" fill="none">
        <path
          d="M4 11a7 7 0 107-7"
          stroke="#ffd43b"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path
          d="M11 4L9 2M11 4l2-2"
          stroke="#ffd43b"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    tag: "Planning",
    title: "Project Planning",
    shortTitle: "Planning",
    desc: "From budget planning to construction coordination, we help keep every stage clear and organized.",
    num: "04",
    angle: 60,
    svgIcon: (
      <svg viewBox="0 0 22 22" fill="none">
        <rect
          x="4"
          y="3"
          width="14"
          height="16"
          rx="1.8"
          stroke="#ffd43b"
          strokeWidth="1.5"
        />
        <line
          x1="7"
          y1="8"
          x2="15"
          y2="8"
          stroke="#ffd43b"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
        <line
          x1="7"
          y1="11.5"
          x2="15"
          y2="11.5"
          stroke="#ffd43b"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
        <line
          x1="7"
          y1="15"
          x2="11"
          y2="15"
          stroke="#ffd43b"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    tag: "Interior",
    title: "Interior Designing",
    shortTitle: "Interior Design",
    desc: "Creative interiors that enhance both beauty and usability of your spaces.",
    num: "05",
    angle: 120,
    svgIcon: (
      <svg viewBox="0 0 22 22" fill="none">
        <path
          d="M11 3a7 7 0 100 14 1.6 1.6 0 001.6-1.6c0-.42-.17-.8-.44-1.08a1.5 1.5 0 01-.44-1.06c0-.83.68-1.5 1.5-1.5H14a4 4 0 004-4c0-3.31-3.13-6-7-6z"
          stroke="#ffd43b"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <circle cx="7.6" cy="9.2" r="1" fill="#ffd43b" />
        <circle cx="10.4" cy="6.8" r="1" fill="#ffd43b" />
        <circle cx="13.6" cy="7.6" r="1" fill="#ffd43b" />
      </svg>
    ),
  },
  {
    tag: "Joint Venture",
    title: "Joint Venture",
    shortTitle: "Joint Venture",
    desc: "Collaborative partnerships to leverage shared resources for successful project delivery.",
    num: "06",
    angle: 180,
    svgIcon: (
      <svg viewBox="0 0 22 22" fill="none">
        <path
          d="M2 11l4-4 3.2 2.4a1.4 1.4 0 001.7 0L14 6l6 5"
          stroke="#ffd43b"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M4 10.5L8 15l2-1.4L12.2 16 16 12"
          stroke="#ffd43b"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 6l2.5-1.5L20 7"
          stroke="#ffd43b"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

const CX = 250,
  CY = 250,
  ORBIT_R = 160,
  LABEL_R = 208;
const TICK_COUNT = 48;
const SPAN_DEG = 48;

function degToRad(d) {
  return (d * Math.PI) / 180;
}

function buildArcPath(angleDeg, r) {
  const a1 = degToRad(angleDeg - SPAN_DEG / 2 - 90);
  const a2 = degToRad(angleDeg + SPAN_DEG / 2 - 90);
  const x1 = CX + r * Math.cos(a1),
    y1 = CY + r * Math.sin(a1);
  const x2 = CX + r * Math.cos(a2),
    y2 = CY + r * Math.sin(a2);
  return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
}

const whyChooseUsPoints = [
  "40+ Years of Experience",
  "Uncompromising Quality",
  "Client-Centric Approach",
  "On-Time, On-Budget Delivery",
  "Experienced & Skilled Team",
  "10-Year Structural Warranty",
];

export default function Services() {
  const [activeIdx, setActiveIdx] = useState(null);
  const stageRef = useRef(null);
  const [stageSize, setStageSize] = useState(500);
  const navigate = useNavigate();
  const location = useLocation();

  const isQuotePage = location.pathname.toLowerCase() === "/quote";

  useEffect(() => {
    if (!stageRef.current) return;
    const ro = new ResizeObserver((entries) => {
      setStageSize(entries[0].contentRect.width);
    });
    ro.observe(stageRef.current);
    setStageSize(stageRef.current.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const handleNodeClick = useCallback((i) => {
    setActiveIdx((prev) => (prev === i ? null : i));
  }, []);

  const handleExploreClick = useCallback(
    (e) => {
      e.preventDefault();

      const scrollToContact = () => {
        const section = document.getElementById("contact");
        if (section) {
          section.scrollIntoView({ behavior: "smooth" });
        }
      };

      if (isQuotePage) {
        navigate("/");
        setTimeout(scrollToContact, 100);
      } else {
        scrollToContact();
      }
    },
    [isQuotePage, navigate],
  );

  const scale = stageSize / 500;
  const active = activeIdx !== null ? services[activeIdx] : null;

  // Tick marks
  const ticks = Array.from({ length: TICK_COUNT }, (_, t) => {
    const a = (t / TICK_COUNT) * Math.PI * 2;
    const r1 = 214,
      r2 = t % 4 === 0 ? 208 : 211;
    return {
      x1: CX + r1 * Math.cos(a),
      y1: CY + r1 * Math.sin(a),
      x2: CX + r2 * Math.cos(a),
      y2: CY + r2 * Math.sin(a),
      major: t % 4 === 0,
    };
  });

  return (
    <section className="services-page-v4" id="services">
      {/* Header */}
      <div className="sv4-top">
        <div className="sv4-intro">
          <p className="sv4-eyebrow">Our Services</p>
          <h1 className="sv4-title">
            Built around <span>your</span> vision.
          </h1>
          <p className="sv4-sub">
            From the first plan to the final handover, Sarrathi Constructions
            provides reliable support for every stage of your project.
          </p>
        </div>
        <div className="sv4-side-labels">
          <span>QUALITY</span>
          <span>PLANNING</span>
          <span>EXECUTION</span>
        </div>
      </div>

      {/* Orbital + Detail */}
      <div className="sv4-orbital-wrap">
        {/* Stage */}
        <div className="sv4-stage" ref={stageRef}>
          {/* SVG rings, arcs, lines */}
          <svg className="sv4-svg" viewBox="0 0 500 500">
            <defs>
              <radialGradient id="sv4-core-grad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#1a3a6e" />
                <stop offset="100%" stopColor="#0c1e3c" />
              </radialGradient>
              <radialGradient id="sv4-glow-grad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#ffd43b" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#ffd43b" stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* Glow backdrop */}
            <circle cx={CX} cy={CY} r="200" fill="url(#sv4-glow-grad)" />

            {/* Pulse rings */}
            <circle
              className="sv4-pulse1"
              cx={CX}
              cy={CY}
              r="86"
              fill="none"
              stroke="#ffd43b"
              strokeWidth="1.5"
            />
            <circle
              className="sv4-pulse2"
              cx={CX}
              cy={CY}
              r="86"
              fill="none"
              stroke="#ffd43b"
              strokeWidth="1.5"
            />

            {/* Outer spinning ring + ticks */}
            <g className="sv4-spin-ring">
              <circle
                cx={CX}
                cy={CY}
                r="218"
                fill="none"
                stroke="rgba(255,255,255,0.05)"
                strokeWidth="1"
              />
              {ticks.map((t, i) => (
                <line
                  key={i}
                  x1={t.x1}
                  y1={t.y1}
                  x2={t.x2}
                  y2={t.y2}
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth={t.major ? "1.5" : "0.8"}
                />
              ))}
            </g>

            {/* Orbit track */}
            <circle
              cx={CX}
              cy={CY}
              r={ORBIT_R}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1.2"
            />

            {/* Arc segments + connector lines per service */}
            {services.map((svc, i) => {
              const isActive = activeIdx === i;
              const rad = degToRad(svc.angle - 90);
              const ex = CX + 82 * Math.cos(rad),
                ey = CY + 82 * Math.sin(rad);
              const tx = CX + 156 * Math.cos(rad),
                ty = CY + 156 * Math.sin(rad);
              const dotX = CX + ORBIT_R * Math.cos(rad),
                dotY = CY + ORBIT_R * Math.sin(rad);
              return (
                <g key={i}>
                  {/* Arc segment */}
                  <path
                    d={buildArcPath(svc.angle, ORBIT_R)}
                    fill="none"
                    stroke={isActive ? "#ffd43b" : "rgba(255,212,59,0.25)"}
                    strokeWidth={isActive ? 3 : 1.5}
                    strokeLinecap="round"
                    style={{ transition: "stroke .3s, stroke-width .3s" }}
                  />
                  {/* Connector */}
                  <line
                    x1={ex}
                    y1={ey}
                    x2={tx}
                    y2={ty}
                    stroke={isActive ? "#ffd43b" : "rgba(255,212,59,0.18)"}
                    strokeWidth={isActive ? 1.8 : 1}
                    strokeDasharray={isActive ? undefined : "3 5"}
                    strokeLinecap="round"
                  />
                  {/* Orbit dot */}
                  <circle
                    cx={dotX}
                    cy={dotY}
                    r={isActive ? 5 : 3.5}
                    fill={isActive ? "#ffd43b" : "rgba(255,212,59,0.4)"}
                    style={{ transition: "r .25s, fill .25s" }}
                  />
                </g>
              );
            })}

            {/* Core circle */}
            <circle cx={CX} cy={CY} r="80" fill="url(#sv4-core-grad)" />
            <circle
              cx={CX}
              cy={CY}
              r="80"
              fill="none"
              stroke="#ffd43b"
              strokeWidth="1.8"
            />
            <circle
              cx={CX}
              cy={CY}
              r="70"
              fill="none"
              stroke="rgba(255,212,59,0.2)"
              strokeWidth="0.8"
            />

            {/* Core text */}
            <text
              x={CX}
              y="234"
              textAnchor="middle"
              fill="#ffd43b"
              fontSize="10"
              fontWeight="900"
              letterSpacing="3"
              fontFamily="sans-serif"
            >
              SARRATHI
            </text>
            <text
              x={CX}
              y="254"
              textAnchor="middle"
              fill="#ffffff"
              fontSize="14"
              fontWeight="900"
              fontFamily="sans-serif"
            >
              CONSTRUCTIONS
            </text>
            <text
              x={CX}
              y="270"
              textAnchor="middle"
              fill="rgba(255,255,255,0.35)"
              fontSize="9"
              letterSpacing="2"
              fontFamily="sans-serif"
            >
              EST. 2010
            </text>
          </svg>

          {/* HTML label nodes — positioned over SVG */}
          {services.map((svc, i) => {
            const isActive = activeIdx === i;
            const rad = degToRad(svc.angle - 90);
            const lx = CX + LABEL_R * Math.cos(rad);
            const ly = CY + LABEL_R * Math.sin(rad);
            return (
              <div
                key={i}
                className={`sv4-node${isActive ? " active" : ""}`}
                style={{ left: lx * scale, top: ly * scale }}
                onClick={() => handleNodeClick(i)}
              >
                <div className="sv4-node-ring">{svc.svgIcon}</div>
                <div className="sv4-node-name">{svc.shortTitle}</div>
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        <div className={`sv4-detail${active ? " visible" : ""}`}>
          {active && (
            <>
              <div className="sv4-dp-num">{active.num}</div>
              <div className="sv4-dp-tag">{active.tag}</div>
              <h3 className="sv4-dp-title">{active.title}</h3>
              <div className="sv4-dp-divider" />
              <p className="sv4-dp-desc">{active.desc}</p>
              <button className="sv4-dp-cta" onClick={handleExploreClick}>
                Reach Us to know more <span>→</span>
              </button>
            </>
          )}
          {!active && <p className="sv4-dp-hint">← Select a service</p>}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="sv4-bottom">
        <div>
          <p className="sv4-bb-label">Why choose us</p>
          <h2 className="sv4-bb-heading">
            Strong foundations. Clear communication. Quality results.
          </h2>
          <p className="sv4-bb-para">
            For over three decades, we've been shaping spaces with quality,
            integrity, and expertise. From homes to commercial and industrial
            developments, every project reflects our commitment to excellence.
            We build with purpose, delivering value that lasts for generations.
          </p>
        </div>
        <div className="sv4-bb-points">
          {whyChooseUsPoints.map((pt) => (
            <div key={pt} className="sv4-bb-point">
              <span className="sv4-bb-tick">✓</span>
              {pt}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
